// send-log-metadata.ts
//
// Per-message send metadata (group/community, message-type, failure, addressing)
// resolved engine-side in the send thunk and stamped onto the outgoing message-log
// action bodies by `api-call-helper.ts`. Mirrors the #ext-version-logging precedent:
// extensionVersion is a GLOBAL read (chrome manifest), so api-call-helper reads it
// directly. This metadata is PER-MESSAGE, so it can't be read globally — the thunk
// stashes it here keyed by messageId, and api-call-helper looks it up by the
// `messageId` already present on every log-action body. Keying by messageId keeps it
// concurrency-safe when several sends are in flight at once.

import type { SendLogMetadata as BaseSendLogMetadata } from '@bt-common/scheduling';
import type { SendEngine } from '../message/raw-send/raw-send-gate';

/** The bt-common shape plus `sendEngine` (#333 slice 6), kept local so no bt-common pin move is
 *  needed. api-call-helper copies every defined field, so the extra key rides through unchanged. */
export type SendLogMetadata = BaseSendLogMetadata & {
    sendEngine?: SendEngine;
    /** #503: true when this tab held a live MQTT connection at attempt start. false = it sent without broker
     *  presence (the server cannot reach it). Absent when the state could not be read. */
    mqttConnected?: boolean;
};

/** #503: add `mqttConnected` (read at attempt start). A throwing reader leaves the field absent. */
export function withMqttConnected(meta: SendLogMetadata, isConnected: () => boolean): SendLogMetadata {
    try {
        return { ...meta, mqttConnected: isConnected() };
    } catch {
        return meta;
    }
}

const store = new Map<string, SendLogMetadata>();

/** Merge partial metadata for a messageId. Called once with the resolved
 *  group/message-type/addressing fields before the send, and again from the
 *  sendFn failure branches to add `sendErrorReason`. */
export function setSendLogMetadata(messageId: string, partial: SendLogMetadata): void {
    if (!messageId) return;
    const prev = store.get(messageId) ?? {};
    store.set(messageId, { ...prev, ...partial });
}

/** Read the accumulated metadata for a messageId (undefined when none stashed). */
export function getSendLogMetadata(messageId: string | undefined): SendLogMetadata | undefined {
    if (!messageId) return undefined;
    return store.get(messageId);
}

/** Drop the entry once the send flow completes — keeps the map bounded. */
export function clearSendLogMetadata(messageId: string | undefined): void {
    if (!messageId) return;
    store.delete(messageId);
}
